import {
  GENDER_COLOURS,
  OFSTED_BORDER_COLOURS,
  normaliseOfstedGradeForPin,
  OFSTED_DEFAULT_BORDER,
  PIN_WIDTH,
  PIN_HEIGHT,
  PIN_HEAD_R,
  HALO_COLOUR,
  HALO_STROKE_WIDTH,
  OFSTED_STROKE_WIDTH,
  FILL_INSET,
  FONT_SIZE,
  STAR_SIZE,
  STAR_COLOUR,
} from "./markerConstants";

const STROKE_ROUND = 'stroke-linejoin="round" stroke-linecap="round"';

export function getPinDimensions() {
  return { width: PIN_WIDTH, height: PIN_HEIGHT };
}

function ofstedBorderLayers(pathD, borderColour) {
  return [
    `<path d="${pathD}" fill="none" stroke="${HALO_COLOUR}" stroke-width="${HALO_STROKE_WIDTH}" ${STROKE_ROUND} />`,
    `<path d="${pathD}" fill="none" stroke="${borderColour}" stroke-width="${OFSTED_STROKE_WIDTH}" ${STROKE_ROUND} />`,
  ].join("");
}

/**
 * Smooth teardrop — cubic curves only (no arc) so strokes stay continuous.
 */
function pinPath(cx, cy, r, tipY) {
  const topY = cy - r;
  const body = tipY - topY;
  const w = r * 1.1;

  return [
    `M ${cx} ${tipY}`,
    `C ${cx - w * 0.42} ${tipY - body * 0.28} ${cx - r * 1.08} ${cy + r * 0.55} ${cx - r} ${cy}`,
    `C ${cx - r} ${cy - r * 0.52} ${cx - r * 0.52} ${topY + r * 0.12} ${cx} ${topY}`,
    `C ${cx + r * 0.52} ${topY + r * 0.12} ${cx + r} ${cy - r * 0.52} ${cx + r} ${cy}`,
    `C ${cx + r * 1.08} ${cy + r * 0.55} ${cx + w * 0.42} ${tipY - body * 0.28} ${cx} ${tipY}`,
    "Z",
  ].join(" ");
}

function starPoints(cx, cy, outerR, innerR, points) {
  const step = Math.PI / points;
  const pts = [];
  for (let i = 0; i < 2 * points; i++) {
    const rad = i % 2 === 0 ? outerR : innerR;
    const angle = i * step - Math.PI / 2;
    pts.push(`${cx + rad * Math.cos(angle)},${cy + rad * Math.sin(angle)}`);
  }
  return pts.join(" ");
}

export function renderSchoolPinSvg({
  gender = "Mixed",
  phase = "S",
  ofstedGrade = "",
  isTopSchool = false,
}) {
  const { width: w, height: h } = getPinDimensions();
  const cx = w / 2;
  const borderPad = HALO_STROKE_WIDTH / 2 + OFSTED_STROKE_WIDTH / 2;
  const r = PIN_HEAD_R;
  const headCy = r + borderPad;
  const tipY = h - 1;

  const ofstedKey = normaliseOfstedGradeForPin(ofstedGrade);
  const borderColour = OFSTED_BORDER_COLOURS[ofstedKey] || OFSTED_DEFAULT_BORDER;
  const borderPath = pinPath(cx, headCy, r, tipY);
  const fillPath = pinPath(
    cx,
    headCy,
    Math.max(r - FILL_INSET, 4),
    tipY - FILL_INSET * 0.5
  );

  let fillDefs = "";
  let fillAttr;

  if (gender === "Mixed") {
    fillDefs = `
      <defs>
        <clipPath id="leftHalf">
          <rect x="0" y="0" width="${cx}" height="${h}" />
        </clipPath>
        <clipPath id="rightHalf">
          <rect x="${cx}" y="0" width="${cx}" height="${h}" />
        </clipPath>
      </defs>`;
    fillAttr = null;
  } else {
    fillAttr = GENDER_COLOURS[gender] || GENDER_COLOURS.Mixed[1];
  }

  let pinFill;
  if (fillAttr) {
    pinFill = [
      `<path d="${fillPath}" fill="${fillAttr}" />`,
      ofstedBorderLayers(borderPath, borderColour),
    ].join("");
  } else {
    const [left, right] = GENDER_COLOURS.Mixed;
    pinFill = [
      `<path d="${fillPath}" fill="${left}" clip-path="url(#leftHalf)" />`,
      `<path d="${fillPath}" fill="${right}" clip-path="url(#rightHalf)" />`,
      ofstedBorderLayers(borderPath, borderColour),
    ].join("");
  }

  const letter = phase
    ? `<text x="${cx}" y="${headCy + FONT_SIZE * 0.36}" text-anchor="middle" fill="#fff" font-size="${FONT_SIZE}" font-weight="700" font-family="system-ui,sans-serif" pointer-events="none">${phase}</text>`
    : "";

  let star = "";
  if (isTopSchool) {
    const sr = STAR_SIZE / 2;
    const sCx = cx + r - 1;
    const sCy = headCy - r + 2;
    star = `<polygon points="${starPoints(sCx, sCy, sr, sr * 0.4, 5)}" fill="${STAR_COLOUR}" stroke="#fff" stroke-width="0.8" />`;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${fillDefs}${pinFill}${letter}${star}</svg>`;
}
